import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import SegmentedTabs from "../components/SegmentedTabs.jsx";
import {
    ARTIFACT_ROLLOUT_PAGE_SIZE,
    applyArtifactRolloutCommand,
    listArtifactRollouts,
    protoBytesToUuid,
    uuidToProtoBytes,
} from "../lib/artifactRolloutClient.js";
import { ENTITLEMENTS, hasEntitlement } from "../lib/entitlements.js";
import { CONTENT_UPDATES, commonValue, groupContentConfigurations, isBundledWatchdog, isContentUpdate } from "../lib/artifactRolloutGroups.js";
import { listDirectoryGroups, listDirectoryIntegrations } from "../lib/identityClient.js";
import { listEndpoints } from "../lib/reportClient.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import ContentUpdatesRolloutRow from "./ContentUpdatesRolloutRow.jsx";

/// One page of synced groups is enough to choose from; the note below the
/// picker says so when there are more.
const DIRECTORY_GROUP_PAGE_SIZE = 200;

// Keep each hostname search bounded; users can narrow the search or paste an ID.
const ENDPOINT_PICKER_LIMIT = 100;

/// Sentinel for the escape hatch. Not a UUID, so it cannot collide with one.
const MANUAL_ENDPOINT_ENTRY = "manual";
const MAX_WAVE_POSITION = 32767;

/// Releases first. A reader opening this page is far more often checking what
/// is reaching their endpoints than changing how it gets there.
const TAB_RELEASES = "releases";
const TAB_FLEET = "fleet";
const TAB_COMPONENTS = "components";

const AUTOMATIC = "ARTIFACT_WAVE_MODE_AUTOMATIC";
const DECLINED = "ARTIFACT_PUBLICATION_STATE_DECLINED";
const GENERAL = "ARTIFACT_AUDIENCE_KIND_GENERAL";
const MANUAL = "ARTIFACT_WAVE_MODE_MANUAL";
const PAUSED = "ARTIFACT_PUBLICATION_STATE_PAUSED";
const PENDING = "ARTIFACT_PUBLICATION_STATE_PENDING";
const ROLLING = "ARTIFACT_PUBLICATION_STATE_ROLLING";
const TENANT_MAIN = "ARTIFACT_AUDIENCE_KIND_TENANT_MAIN";
const TENANT_PREVIEW = "ARTIFACT_AUDIENCE_KIND_TENANT_PREVIEW";
const TENANT_TARGET = "ARTIFACT_AUDIENCE_KIND_TENANT_TARGET";

function label(value) {
    return String(value || "unspecified")
        .replace(/^ARTIFACT_[A-Z]+_(KIND_|STATE_|MODE_|POLICY_)?/, "")
        .replaceAll("_", " ")
        .toLowerCase();
}

function directoryGroupLabel(rule) {
    const name = rule.displayName?.trim();
    return name ? `${name} (${rule.directoryGroupId})` : rule.directoryGroupId;
}

function count(value) {
    return String(value ?? "0");
}

const POLICY_AUTOMATIC = "ARTIFACT_PUBLICATION_POLICY_AUTO_ALL";
const POLICY_AUTO_MINOR = "ARTIFACT_PUBLICATION_POLICY_AUTO_MINOR";
const POLICY_MANUAL = "ARTIFACT_PUBLICATION_POLICY_MANUAL";

/// Collapse the stored policy onto the two choices that differ in behaviour.
///
/// The stored enum has three values, but each is read for one release class
/// only: `minor_policy` is consulted for a minor release, where both
/// `auto_all` and `auto_minor` mean "publish automatically", and
/// `major_policy` for a major, where `auto_minor` means the same as `manual`.
/// So per class there are exactly two outcomes, and offering three options
/// asked the reader to choose between two that behave identically.
function policyChoice(policy, releaseClass) {
    if (policy === POLICY_AUTOMATIC) return POLICY_AUTOMATIC;
    if (policy === POLICY_AUTO_MINOR && releaseClass === "minor") return POLICY_AUTOMATIC;
    return POLICY_MANUAL;
}

/// Parse a `percent:seconds` ladder.
///
/// Returns null rather than throwing, because this runs on every keystroke to
/// preview what was typed. The same encoding the operator console accepts, so
/// a ladder can be moved between them verbatim.
const WAVE_UNITS = [
    { label: "seconds", seconds: 1 },
    { label: "minutes", seconds: 60 },
    { label: "hours", seconds: 3600 },
    { label: "days", seconds: 86400 },
];

/// Show a wait in the largest unit that divides it exactly, so 300 reads as
/// 5 minutes and 90 stays 90 seconds rather than becoming 1.5 minutes.
export function secondsToRow(bakeSeconds) {
    const total = Number(bakeSeconds) || 0;
    const unit =
        [...WAVE_UNITS].reverse().find((entry) => total >= entry.seconds && total % entry.seconds === 0) ??
        WAVE_UNITS[0];
    return { amount: String(total / unit.seconds), unit: unit.label };
}

export function rowToSeconds(row) {
    const unit = WAVE_UNITS.find((entry) => entry.label === row.unit) ?? WAVE_UNITS[0];
    return Number(row.amount) * unit.seconds;
}

/// Rows to the wire encoding, so one validator covers both the rows a reader
/// edits and a schedule arriving from the server.
export function rowsToLadder(rows) {
    return (rows ?? [])
        .map((row) => `${String(row.percent).trim()}:${rowToSeconds(row)}`)
        .join(",");
}

export function stepsToRows(steps) {
    return (steps ?? []).map((step) => ({
        percent: String(step.cumulativePercent),
        ...secondsToRow(step.bakeSeconds),
    }));
}

export function parseWaveSteps(text) {
    const parts = String(text ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.length === 0) return null;
    const steps = [];
    for (const [index, part] of parts.entries()) {
        const match = /^(\d{1,3}):(\d{1,6})$/.exec(part);
        if (!match) return null;
        const cumulativePercent = Number(match[1]);
        const bakeSeconds = Number(match[2]);
        if (cumulativePercent < 1 || cumulativePercent > 100) return null;
        // Cumulative means strictly increasing; a flat or falling step would
        // expose nobody new and never complete.
        if (index > 0 && cumulativePercent <= steps[index - 1].cumulativePercent) return null;
        steps.push({ bakeSeconds, cumulativePercent, position: index + 1 });
    }
    return steps.at(-1).cumulativePercent === 100 ? steps : null;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/// Enough of an identifier to recognize and correlate, without pretending the
/// whole thing is readable.
function shortId(value) {
    return UUID_PATTERN.test(value) ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
}

/// Turn a component key into something a person can read.
///
/// The keys are addresses, not names: `agent:agent`, and for the other kinds a
/// bare UUID after the prefix. `displayName` carries the name the component
/// actually has, resolved server-side: a Managed Skill's is the tenant's own,
/// and a content module's is the provider tag from its signed manifest.
/// Without one there is no name to show, so the kind and a shortened
/// identifier remain the honest rendering.
///
/// Content modules share one dashboard entry; Overseer exposes module identity.
function componentLabel(componentKey, componentKind, displayName) {
    if (componentKey === CONTENT_UPDATES) return "Content updates";
    const key = String(componentKey ?? "");
    const separator = key.indexOf(":");
    const suffix = separator === -1 ? key : key.slice(separator + 1);
    const name = String(displayName ?? "").trim();
    switch (componentKind) {
        case "ARTIFACT_COMPONENT_KIND_AGENT":
            return `Origin ${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`;
        case "ARTIFACT_COMPONENT_KIND_WASM":
            return "Content updates";
        case "ARTIFACT_COMPONENT_KIND_MANAGED_SKILL":
            return `Managed Skill ${name || shortId(suffix)}`;
        default:
            return key || "unknown component";
    }
}

/// Spell a count out with its unit, so "0" reads as a real answer rather than
/// looking like a missing value.
function plural(count, unit) {
    return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

/// Render a bake as a duration a reader can judge, not a raw second count.
///
/// Always a duration, including zero: an earlier version collapsed 0 to "no
/// wait", which the surrounding sentence turned into "then wait no wait".
function bakeLabel(seconds) {
    const total = Number(seconds ?? 0);
    if (!Number.isFinite(total) || total <= 0) return plural(0, "second");
    const whole = Math.trunc(total);
    if (whole < 60) return plural(whole, "second");
    const minutes = Math.floor(whole / 60);
    const remainderSeconds = whole % 60;
    if (minutes < 60) {
        return remainderSeconds
            ? `${plural(minutes, "minute")} ${plural(remainderSeconds, "second")}`
            : plural(minutes, "minute");
    }
    const hours = Math.floor(minutes / 60);
    const remainderMinutes = minutes % 60;
    return remainderMinutes
        ? `${plural(hours, "hour")} ${plural(remainderMinutes, "minute")}`
        : plural(hours, "hour");
}

/// The round-trip encoding the schedule box accepts. Shared with the card's
/// edit button so a loaded schedule is exactly what the editor would emit.
export function formatWaveSteps(steps) {
    return (steps ?? [])
        .map((step) => `${step.cumulativePercent}:${step.bakeSeconds}`)
        .join(",");
}

function stepLabel(step, unit, isLast) {
    const share = `${step.cumulativePercent}% of ${unit}`;
    return `${share}, then wait ${bakeLabel(step.bakeSeconds)} before ${isLast ? "completion" : "advancing"}`;
}

function StepList({ steps, unit }) {
    const list = steps ?? [];
    return (
        <ol className="mt-1 grid gap-0.5">
            {list.map((step, index) => (
                <li key={`${step.cumulativePercent}-${index}`} className="flex gap-2">
                    <span className="text-[color:var(--muted-soft)]">{index + 1}.</span>
                    <span>{stepLabel(step, unit, index === list.length - 1)}</span>
                </li>
            ))}
        </ol>
    );
}

function timestamp(value) {
    if (!value) return "";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

const inputClass = "h-[30px] min-w-0 border border-[color:var(--line)] bg-[color:var(--control-bg)] px-2 text-[11px] text-[color:var(--ink)] outline-none focus:border-[color:var(--green)]";
const buttonClass = "h-[28px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-2 text-[10px] font-medium text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:cursor-not-allowed disabled:opacity-40";

// The gRPC code the command service returns when a tenant lacks the
// `artifact_rollout_control` entitlement: api-server answers
// `Status::permission_denied("artifact rollout control entitlement is
// required")`. Deliberately NOT `FAILED_PRECONDITION`, which is what
// `StateConflict` and an inactive cutover map to, so keying on that would both
// miss every real entitlement refusal and label every ordinary state conflict
// as one.
//
// Still handled even though the controls are now hidden from a tenant known to
// lack the entitlement: the entitlement can be revoked while the page is open,
// after the entitlement was confirmed.
const ENTITLEMENT_REFUSAL_CODE = "PERMISSION_DENIED";

export default function ArtifactRolloutsPage({ entitlements = null }) {
    if (!hasEntitlement(entitlements, ENTITLEMENTS.ARTIFACT_ROLLOUT_CONTROL)) return null;
    return <ArtifactRolloutControls legacyUpdatesBlocked={hasEntitlement(entitlements, ENTITLEMENTS.DISABLE_AUTO_UPDATE)} />;
}

function ArtifactRolloutControls({ legacyUpdatesBlocked }) {
    const caps = useMyPermissions();
    const canControl = hasPermission(caps, PERMISSIONS.TENANT_CONFIG_WRITE);
    const [unentitled, setUnentitled] = useState(false);
    const [data, setData] = useState({
        assignmentRulesTruncated: false,
        configurations: [],
        directoryGroupRules: [],
        endpointRules: [],
        policies: [],
        rollouts: [],
        nextPageToken: "",
        tracks: [],
    });
    const [showHistory, setShowHistory] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const [componentId, setComponentId] = useState("");
    const [majorPolicy, setMajorPolicy] = useState(POLICY_MANUAL);
    const [minorPolicy, setMinorPolicy] = useState(POLICY_MANUAL);
    const [endpointId, setEndpointId] = useState("");
    const [trackId, setTrackId] = useState("");
    const [wavePosition, setWavePosition] = useState("1");
    const [directoryGroupId, setDirectoryGroupId] = useState("");
    const [directoryIntegrationId, setDirectoryIntegrationId] = useState("");
    // Synced directory data, so the override targets a group by name instead of
    // a pasted UUID. A group that is not synced has no resolvable membership,
    // so the picker is also the accurate set of things a rule can act on.
    const [integrations, setIntegrations] = useState([]);
    const [groups, setGroups] = useState({ items: [], truncated: false });
    const [directoryUnavailable, setDirectoryUnavailable] = useState(false);

    useEffect(() => {
        let active = true;
        void (async () => {
            try {
                const response = await listDirectoryIntegrations();
                if (active) setIntegrations(response?.integrations ?? []);
            } catch {
                // Directory integrations are a separate feature; failing to read
                // them must not take out the rollout page.
                if (active) setDirectoryUnavailable(true);
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    const [endpointOptions, setEndpointOptions] = useState({ items: [], total: 0 });
    const [endpointEntryManual, setEndpointEntryManual] = useState(false);
    const [endpointQuery, setEndpointQuery] = useState("");
    const [endpointLoading, setEndpointLoading] = useState(false);
    const [endpointError, setEndpointError] = useState("");
    // "" is the tenant-wide schedule; a component id scopes the editor to one
    // component, which outranks the tenant-wide one for that component only.
    const [scheduleScope, setScheduleScope] = useState("");
    const [waveRows, setWaveRows] = useState([]);
    const [pendingConfirm, setPendingConfirm] = useState(null);
    // The select shows the manual sentinel while a typed id is in play, so the
    // two controls never disagree about what is selected.
    const endpointPickerValue = endpointEntryManual ? MANUAL_ENDPOINT_ENTRY : endpointId;

    useEffect(() => {
        let active = true;
        setEndpointLoading(true);
        setEndpointError("");
        const search = async () => {
            try {
                const response = await listEndpoints({ page: 0, pageSize: ENDPOINT_PICKER_LIMIT, hostname: endpointQuery.trim() || undefined });
                if (!active) return;
                setEndpointOptions({
                    items: (response?.endpoints ?? []).map((endpoint) => ({
                        ...endpoint,
                        id: protoBytesToUuid(endpoint.id),
                    })),
                    total: response?.total ?? 0,
                });
            } catch {
                if (active) {
                    setEndpointOptions({ items: [], total: 0 });
                    setEndpointError("Endpoints could not be loaded. Change the search to retry, or enter an ID manually.");
                }
            } finally {
                if (active) setEndpointLoading(false);
            }
        };
        const timer = endpointQuery ? setTimeout(search, 300) : null;
        if (!endpointQuery) void search();
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [endpointQuery]);

    useEffect(() => {
        if (!directoryIntegrationId) {
            setGroups({ items: [], truncated: false });
            return undefined;
        }
        let active = true;
        void (async () => {
            try {
                const response = await listDirectoryGroups({
                    integration_id: directoryIntegrationId,
                    page_size: DIRECTORY_GROUP_PAGE_SIZE,
                });
                if (!active) return;
                setGroups({
                    items: response?.groups ?? [],
                    truncated: Boolean(response?.nextPageToken),
                });
            } catch {
                if (active) setGroups({ items: [], truncated: false });
            }
        })();
        return () => {
            active = false;
        };
    }, [directoryIntegrationId]);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await listArtifactRollouts();
            setData({
                assignmentRulesTruncated: response?.assignmentRulesTruncated ?? false,
                configurations: response?.configurations ?? [],
                directoryGroupRules: response?.directoryGroupRules ?? [],
                endpointRules: response?.endpointRules ?? [],
                policies: response?.policies ?? [],
                rollouts: response?.rollouts ?? [],
                nextPageToken: response?.nextPageToken ?? "",
                tracks: response?.tracks ?? [],
            });
        } catch (cause) {
            setError(cause?.serverMessage || cause?.message || String(cause));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const [loadingMore, setLoadingMore] = useState(false);
    // Only the rollout list is cursor-paged; every sibling list in the response
    // is capped server-side, so a later page must not replace them.
    const loadMore = useCallback(async () => {
        if (!data.nextPageToken) return;
        setLoadingMore(true);
        setError("");
        try {
            const response = await listArtifactRollouts({ pageToken: data.nextPageToken });
            setData((current) => ({
                ...current,
                rollouts: [...current.rollouts, ...(response?.rollouts ?? [])],
                nextPageToken: response?.nextPageToken ?? "",
            }));
        } catch (cause) {
            setError(cause?.serverMessage || cause?.message || String(cause));
        } finally {
            setLoadingMore(false);
        }
    }, [data.nextPageToken]);

    // `value` may be a builder rather than a value. `uuidToProtoBytes` throws
    // on a malformed UUID, and when the argument object was built at the call
    // site that throw happened before this function was entered, so the catch
    // below never saw it: the operator got a dead button and no message.
    // Building inside the try puts input validation on the same error path as
    // the request.
    const run = useCallback(async (name, value) => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setError("");
        setUnentitled(false);
        let completed = 0;
        let total = 1;
        try {
            const argument = typeof value === "function" ? value() : value;
            const commands = Array.isArray(argument) ? argument : [argument];
            total = commands.length;
            for (const command of commands) {
                await applyArtifactRolloutCommand(typeof name === "function" ? name(command) : name, command);
                completed += 1;
            }
            await refresh();
        } catch (cause) {
            if (completed) await refresh();
            if (completed) setError(`${completed} of ${total} changes saved. Review the current settings before retrying.`);
            // The server is the entitlement authority. Every command here
            // except the Managed Skill ones needs `artifact_rollout_control`,
            // and TENANT_CONFIG_WRITE alone does not imply it. The controls are
            // hidden until the entitlement is confirmed, but it can still be
            // revoked after the page mounts.
            //
            // Keyed on the gRPC code, not the message: the refusal text is
            // English prose that can be reworded without this page's tests
            // noticing, which would leave an unentitled tenant staring at a
            // raw error string instead of the reason.
            if (cause?.grpcCode === ENTITLEMENT_REFUSAL_CODE) {
                setUnentitled(true);
                return;
            }
            const detail = cause?.serverMessage || cause?.message || String(cause);
            setError(total > 1 ? `${completed} of ${total} changes saved. Review the current settings before retrying. ${detail}` : detail);
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    }, [refresh]);

    const configurations = useMemo(() => groupContentConfigurations(data.configurations), [data.configurations]);
    const contentComponentIds = useMemo(() => [...new Set(
        [...data.configurations, ...data.rollouts].filter(isContentUpdate).map((entry) => entry.componentId),
    )].sort(), [data.configurations, data.rollouts]);
    const scopeIds = useCallback((scope) => scope === CONTENT_UPDATES ? contentComponentIds : [scope], [contentComponentIds]);

    const components = useMemo(() => {
        const unique = new Map();
        for (const rollout of [...data.configurations, ...data.rollouts]) {
            if (isBundledWatchdog(rollout)) continue;
            unique.set(
                isContentUpdate(rollout) ? CONTENT_UPDATES : rollout.componentId,
                componentLabel(rollout.componentKey, rollout.componentKind, rollout.displayName)
                    || protoBytesToUuid(rollout.componentId),
            );
        }
        return [...unique.entries()].sort((left, right) => left[1].localeCompare(right[1]));
    }, [data.configurations, data.rollouts]);

    const previewTrack = data.tracks.find((track) => track.trackKind === "ARTIFACT_TRACK_KIND_PREVIEW");
    const previewEnabled = Boolean(componentId && scopeIds(componentId).every((id) => previewTrack?.enabledComponentIds?.includes(id)));
    const previewMixed = componentId === CONTENT_UPDATES && !previewEnabled && contentComponentIds.some((id) => previewTrack?.enabledComponentIds?.includes(id));

    // Without this the selects always showed their defaults, so opening the page
    // and pressing Save quietly replaced a configured policy with those
    // defaults. A control that misreports the current setting is worse than no
    // control.
    useEffect(() => {
        if (!componentId) return;
        const saved = scopeIds(componentId).map((id) => data.policies.find((policy) => policy.componentId === id));
        setMajorPolicy(commonValue(saved.map((policy) => policyChoice(policy?.majorPolicy, "major"))));
        setMinorPolicy(commonValue(saved.map((policy) => policyChoice(policy?.minorPolicy, "minor"))));
    }, [componentId, data.policies, scopeIds]);

    // Seed from the ladder in force. A tenant ladder applies to every
    // component, so once one is set they all resolve to it and the set is
    // uniform; a mixed set means this tenant is still on Origin's
    // per-component defaults, which is worth saying rather than picking one
    // arbitrarily and calling it "current".
    const ladders = useMemo(
        () =>
            data.configurations.filter((entry) => !isBundledWatchdog(entry)).map((entry) => formatWaveSteps(entry.waveSteps)),
        [data.configurations],
    );
    const laddersAgree = ladders.length > 0 && ladders.every((entry) => entry === ladders[0]);

    // Seeding is driven by fresh data and by explicit actions, never by the
    // scope state itself. Depending on `scheduleScope` meant an action that
    // set the scope and the rows together had its rows overwritten by this
    // effect a moment later: pressing Edit on one card while scoped to
    // another emptied the editor instead of loading that card.
    const scopeRef = useRef("");
    const seedRowsFor = useCallback(
        (scope) => {
            scopeRef.current = scope;
            if (scope) {
                const scoped = configurations.find(
                    (entry) => entry.componentId === scope,
                );
                setWaveRows(stepsToRows(scoped?.waveSteps));
                return;
            }
            setWaveRows(
                laddersAgree ? stepsToRows(data.configurations.find((entry) => !isBundledWatchdog(entry))?.waveSteps) : [],
            );
        },
        [configurations, data.configurations, laddersAgree],
    );

    useEffect(() => {
        seedRowsFor(scopeRef.current);
    }, [seedRowsFor]);

    const [groupTrackId, setGroupTrackId] = useState("");
    const [groupWavePosition, setGroupWavePosition] = useState("1");
    const [tab, setTab] = useState(TAB_RELEASES);
    const editorRef = useRef(null);

    // The editor sits above the cards, so filling it from a card below is a
    // change the reader cannot see. Bring it into view as well as filling it.
    const editScheduleRows = useCallback(
        (configuration, scoped) => {
            const scope = scoped ? configuration.componentId : "";
            setScheduleScope(scope);
            scopeRef.current = scope;
            // Seeded from the card that was pressed, which for an unscoped
            // edit is not what the scope alone would produce.
            setWaveRows(stepsToRows(configuration.waveSteps));
            editorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        },
        [],
    );

    const changeScheduleScope = useCallback(
        (scope) => {
            setScheduleScope(scope);
            seedRowsFor(scope);
        },
        [seedRowsFor],
    );

    const setRow = useCallback((index, patch) => {
        setWaveRows((rows) => rows.map((row, at) => (at === index ? { ...row, ...patch } : row)));
    }, []);
    const removeRow = useCallback((index) => {
        setWaveRows((rows) => rows.filter((_, at) => at !== index));
    }, []);
    // A new wave starts at 100 because the last one has to, and a reader
    // adding a step is usually inserting before the finish rather than
    // extending past it.
    const addRow = useCallback(() => {
        setWaveRows((rows) => [...rows, { amount: "0", percent: "100", unit: "seconds" }]);
    }, []);

    // The per-component tab is entirely write controls, so it has nothing to
    // show an operator without write permission. The fleet tab still shows
    // the schedule in force and the existing rules to entitled readers.
    const tabs = useMemo(
        () => [
            { id: TAB_RELEASES, eyebrow: "Releases", title: "Choose a version" },
            { id: TAB_FLEET, eyebrow: "Your fleet", title: "Schedule and overrides" },
            ...(canControl
                ? [{ id: TAB_COMPONENTS, eyebrow: "Per component", title: "Policy and tracks" }]
                : []),
        ],
        [canControl],
    );

    const visibleRollouts = data.rollouts.filter((rollout) => {
        if (isBundledWatchdog(rollout)) return false;
        const active = rollout.audienceKind !== GENERAL && [PENDING, DECLINED, ROLLING, PAUSED].includes(rollout.publicationState);
        return showHistory ? !active : active;
    });

    const contentRollouts = visibleRollouts.filter(isContentUpdate);
    const parsedLadder = parseWaveSteps(rowsToLadder(waveRows));

    const shortestLadder = useMemo(() => {
        let shortest = null;
        for (const entry of data.configurations) {
            const steps = entry.waveSteps ?? [];
            if (steps.length > 0 && (shortest === null || steps.length < shortest.length)) {
                shortest = steps;
            }
        }
        return shortest;
    }, [data.configurations]);
    const isWavePosition = (value) =>
        Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= MAX_WAVE_POSITION;
    const wavePositionValid = isWavePosition(wavePosition);
    const groupWavePositionValid = isWavePosition(groupWavePosition);
    // The page starts with no rows when components disagree, so reporting that
    // as invalid input would blame the reader for something they never typed.
    const ladderHint = parsedLadder
        ? `${plural(parsedLadder.length, "wave")}. ${parsedLadder
              .map((step, index) => stepLabel(step, "your endpoints", index === parsedLadder.length - 1))
              .join("; ")}`
        : waveRows.length === 0
          ? ladders.length > 0 && !laddersAgree
              ? "Your components are on different schedules, so there is no single one to start from. Add waves here, or use Edit this schedule on any component below to load its schedule."
              : "Add a wave to start."
          : "Not a schedule yet. Each share must be larger than the one above it, and the last must be 100%.";

    // Saving moves every component onto one schedule, so it names the ones
    // that are on a different one first. Re-saving what is already in force
    // moves nothing and asks nothing.
    const saveCommand = (steps) =>
        scheduleScope
            ? scopeIds(scheduleScope).map((id) => ({ componentId: id, name: "component", steps }))
            : { name: "tenant", steps };

    const saveWaveLadder = () => {
        if (!parsedLadder) return;
        const ladder = rowsToLadder(waveRows);
        const changes = (entry) => formatWaveSteps(entry.waveSteps) !== ladder;
        // A component on its own schedule does not move when the tenant-wide
        // one is saved, so naming it would be a false warning.
        const moved = [...new Set(data.configurations
            .filter((entry) =>
                scheduleScope
                    ? scopeIds(scheduleScope).includes(entry.componentId)
                    : entry.waveScope !== "tenant_component",
            )
            .filter(changes)
            .map((entry) =>
                componentLabel(entry.componentKey, entry.componentKind, entry.displayName),
            ))];
        // One component changing is the reader's own explicit choice, so it
        // needs no warning; a tenant-wide save reaching several does.
        if (moved.length === 0 || scheduleScope) {
            void run("setWaveProfile", saveCommand(parsedLadder));
            return;
        }
        setPendingConfirm({ kind: "save", moved, steps: parsedLadder });
    };

    // Components that follow the tenant-wide schedule are the ones removing it
    // moves; a component with its own keeps it, and one already on an Origin
    // default is already where this would put it.
    const followsGlobalSchedule = data.configurations.filter(
        (entry) => entry.waveScope === "tenant",
    );

    const clearGlobalSchedule = () =>
        setPendingConfirm({
            kind: "clear",
            moved: [...new Set(followsGlobalSchedule.map((entry) =>
                componentLabel(entry.componentKey, entry.componentKind, entry.displayName),
            ))],
        });

    const confirmPendingAction = () => {
        const pending = pendingConfirm;
        setPendingConfirm(null);
        if (!pending) return;
        if (pending.kind === "clear") {
            // No component: the tenant-wide profile itself.
            void run("clearWaveProfile", {});
            return;
        }
        void run("setWaveProfile", saveCommand(pending.steps));
    };

    const useGlobalSchedule = (componentId) =>
        run("clearWaveProfile", scopeIds(componentId)
            .filter((id) => data.configurations.some((entry) => entry.componentId === id && entry.waveScope === "tenant_component"))
            .map((id) => ({ componentId: id })));

    const setPolicy = () => run("setPublicationPolicy", scopeIds(componentId).map((id) => ({
        componentId: id,
        majorPolicy,
        minorPolicy,
    })));

    const togglePreview = () => {
        if (!componentId) return;
        if (previewEnabled && !window.confirm("Disable preview for this component? Installed software is not downgraded; endpoints may remain ahead of main until main catches up.")) return;
        void run("enablePreviewTrack", scopeIds(componentId).map((id) => ({ componentId: id, enabled: !previewEnabled })));
    };

    const setEndpointTrack = () => run("setEndpointTrack", () => ({
        endpointId: uuidToProtoBytes(endpointId),
        trackId: trackId || undefined,
    }));

    const setEndpointWave = () => run("setEndpointWave", () => ({
        endpointId: uuidToProtoBytes(endpointId),
        wavePosition: Number(wavePosition),
    }));

    const setDirectoryGroupTrack = () => run("setDirectoryGroupTrack", () => ({
        directoryGroupId,
        directoryIntegrationId,
        trackId: groupTrackId || undefined,
    }));

    const setDirectoryGroupWave = () => run("setDirectoryGroupWave", () => ({
        directoryGroupId,
        directoryIntegrationId,
        wavePosition: Number(groupWavePosition),
    }));

    return (
        <>
            <AdminViewHeader title="Artifact Rollouts" />
            <div className="flex-1 min-h-0 overflow-y-auto py-4">
                <fieldset disabled={saving} aria-busy={loading || saving} className="grid min-w-0 gap-4">
                    {saving && <div role="status" className="text-[11px] text-[color:var(--muted)]">Saving changes...</div>}
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h1 className="text-[18px] font-medium text-[color:var(--ink)]">Artifact rollouts</h1>
                            <p className="mt-1 max-w-[720px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                Origin ships Agent and Content Updates to the fleet gradually. This page decides how they
                                reach your endpoints once they arrive: whether a release starts on its own or waits for
                                you, which endpoints see it first, and how to stop one that is already moving. Managed
                                Skills roll out the same way, but you deploy those from Skills.
                            </p>
                        </div>
                        <button type="button" className={buttonClass} onClick={() => void refresh()}>Refresh</button>
                    </div>

                    {legacyUpdatesBlocked && (
                        <div role="status" className="border border-[color:var(--line)] bg-[color:var(--panel)] px-3 py-2 text-[11px] text-[color:var(--ink)]">
                            Agent updates are blocked by your legacy update setting. Before removing the block,
                            your Origin administrator must confirm that existing releases require your approval.
                        </div>
                    )}

                    {error && (
                        <div className="border border-[color:var(--danger)]/40 bg-[color:var(--danger)]/10 px-3 py-2 text-[11px] text-[color:var(--danger)]">
                            {error}
                        </div>
                    )}

                    {unentitled && (
                        <div className="border border-[color:var(--line)] bg-[color:var(--panel)] px-3 py-2 text-[11px] text-[color:var(--muted)]">
                            Release rollout controls need the artifact rollout control entitlement
                            for this tenant. Managed Skill deployments are unaffected. Contact your
                            Origin representative to enable it.
                        </div>
                    )}

                    <SegmentedTabs tabs={tabs} active={tab} onSelect={setTab} />

                    {tab === TAB_RELEASES && (
                        <div className="grid gap-4">
                    <div>
                        <h2 className="text-[12px] font-medium text-[color:var(--ink)]">{showHistory ? "Rollout history" : "Available updates"}</h2>
                        <p className="mt-1 max-w-[720px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                            Choose the version you want to start. Releases waiting for approval do not reach your endpoints.
                            Starting a version opens its first wave; later versions still need their own approval when your
                            release policy is manual. Pause stops new waves. Hold also stops delivery to endpoints already
                            admitted. Neither removes software already installed.
                        </p>
                        <div className="mt-2 flex gap-2">
                            <button type="button" className={`${buttonClass} aria-pressed:bg-[color:var(--green-soft)] aria-pressed:text-[color:var(--ink)]`} aria-pressed={!showHistory} onClick={() => setShowHistory(false)}>Available updates</button>
                            <button type="button" className={`${buttonClass} aria-pressed:bg-[color:var(--green-soft)] aria-pressed:text-[color:var(--ink)]`} aria-pressed={showHistory} onClick={() => setShowHistory(true)}>Rollout history</button>
                        </div>
                    </div>

                    <div className="overflow-x-auto border border-[color:var(--line)]">
                        <table className="w-full min-w-[980px] border-collapse text-left text-[11px]">
                            <thead className="bg-[color:var(--panel)] text-[9px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">
                                <tr>
                                    <th className="px-3 py-2">Component</th>
                                    <th className="px-3 py-2">Version</th>
                                    <th className="px-3 py-2">Audience</th>
                                    <th className="px-3 py-2">State</th>
                                    <th className="px-3 py-2" title="Wave eligibility, not installed versions">Endpoints eligible / active</th>
                                    <th className="px-3 py-2" title="How waves advance after you start a release">Wave mode</th>
                                    <th className="px-3 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[color:var(--line)]">
                                {contentRollouts.length > 0 && <ContentUpdatesRolloutRow allRows={data.rollouts.filter(isContentUpdate)} buttonClass={buttonClass} canControl={canControl && !showHistory} onRun={run} rows={contentRollouts} />}
                                {visibleRollouts.filter((rollout) => !isContentUpdate(rollout)).map((rollout) => {
                                    const state = rollout.publicationState;
                                    const publicationId = rollout.publicationId;
                                    const releaseId = rollout.releaseId;
                                    const manual = rollout.waveMode === MANUAL;
                                    return (
                                        <tr key={publicationId}>
                                            <td className="px-3 py-2 text-[color:var(--ink)]">
                                                {componentLabel(rollout.componentKey, rollout.componentKind, rollout.displayName)}
                                                <div className="font-mono text-[9px] text-[color:var(--muted-soft)]" title={rollout.componentKey}>{rollout.componentKey}</div>
                                            </td>
                                            <td className="px-3 py-2 font-mono text-[color:var(--ink)]">{rollout.version}</td>
                                            <td className="px-3 py-2 text-[color:var(--muted)]">{rollout.audienceKind === GENERAL ? "Origin general rollout" : label(rollout.audienceKind)}</td>
                                            <td className="px-3 py-2 text-[color:var(--muted)]">
                                                {state === PENDING && rollout.audienceKind === TENANT_MAIN && !rollout.awaitingGeneralCompletion
                                                    ? "Waiting for your approval"
                                                    : label(state)}
                                                {rollout.held && <div className="font-medium">Delivery blocked by a hold</div>}
                                                {rollout.audienceKind === GENERAL && <div className="text-[9px] text-[color:var(--muted-soft)]">Origin's rollout status. This does not confirm installation on your endpoints.</div>}
                                                {rollout.awaitingGeneralCompletion && <div className="text-[9px] text-[color:var(--muted-soft)]">Origin is still rolling this release out generally. It can be started here once that finishes.</div>}
                                                {state === DECLINED && <div className="text-[9px] text-[color:var(--muted-soft)]">Declined. Start it later to take it anyway.</div>}
                                                {/* Says which controls stop applying rather than all of them:
                                                    a forced replacement crosses the fleet on the tenant's own
                                                    wave schedule, so claiming their controls do not apply was
                                                    both alarming and wrong. */}
                                                {rollout.systemEnforced && <div className="text-[9px] text-[color:var(--muted-soft)]">Required support update, because {rollout.replacedVersion ? `${rollout.replacedVersion} is` : "the release you were on is"} no longer supported. This one cannot be declined or held, but it still follows your wave schedule.</div>}
                                                {rollout.retirementDeadline && <div className="text-[9px] text-[color:var(--muted-soft)]">retirement due {timestamp(rollout.retirementDeadline)}</div>}
                                                {/* Without this a tenant watches a retirement date pass and
                                                    nothing happen. Origin will not retire a release until a
                                                    newer one covers everything it does, which for a component
                                                    that never gets a successor is forever. */}
                                                {rollout.retirementBlocked && <div className="text-[9px] text-[color:var(--muted-soft)]">Staying in place past that date. Origin retires a release only once a newer one covering everything this one does has finished rolling out.</div>}
                                            </td>
                                            <td className="px-3 py-2 text-[color:var(--muted)]">
                                                {[PENDING, DECLINED].includes(state) ? "Not started" : <>
                                                    {count(rollout.eligibleEndpointCount)} / {count(rollout.activeEndpointCount)}
                                                    <div className="text-[9px] text-[color:var(--muted-soft)]">position {rollout.currentPosition ?? 0}</div>
                                                </>}
                                                {rollout.componentKind === "ARTIFACT_COMPONENT_KIND_MANAGED_SKILL" && <div className="text-[9px] text-[color:var(--muted-soft)]">{count(rollout.appliedEndpointCount)} applied</div>}
                                            </td>
                                            <td className="px-3 py-2 text-[color:var(--muted)]">{label(rollout.waveMode)}</td>
                                            <td className="px-3 py-2">
                                                {canControl && rollout.audienceKind !== GENERAL && !rollout.systemEnforced && (
                                                    <div className="flex flex-wrap gap-1">
                                                        {/* Main only. A preview track follows the latest promoted
                                                            release on its own: enabling it creates the publication and
                                                            the scheduler starts it with no policy gate, so there is
                                                            nothing here for an operator to start. Rendering Start on a
                                                            preview row pointed it at the main-release command. */}
                                                        {/* A declined row is revivable: `publish_release` short-circuits
                                                            only on rolling, paused and completed, so Start on a declined
                                                            publication restarts it. Withholding the button here made a
                                                            decline look permanent when it is not.

                                                            Start is withheld while Origin's general rollout is unfinished,
                                                            because the same publish call refuses that outright. */}
                                                        {[PENDING, DECLINED].includes(state) && rollout.audienceKind === TENANT_MAIN && !rollout.awaitingGeneralCompletion && <button type="button" className={buttonClass} onClick={() => void run("publishTenantMainRelease", { releaseId, waveMode: AUTOMATIC })}>Start</button>}
                                                        {state === PENDING && <button type="button" className={buttonClass} onClick={() => void run("declinePublication", { publicationId })}>Decline</button>}
                                                        {(state === ROLLING || state === PAUSED) && <button type="button" className={buttonClass} onClick={() => void run(state === PAUSED ? "resumePublication" : "pausePublication", { publicationId })}>{state === PAUSED ? "Resume" : "Pause"}</button>}
                                                        {(state === ROLLING || state === PAUSED) && <button type="button" className={buttonClass} onClick={() => void run("setWaveMode", { publicationId, waveMode: manual ? AUTOMATIC : MANUAL })}>{manual ? "Automatic" : "Manual"}</button>}
                                                        {rollout.holdClearPending && <span>Release hold clear pending</span>}
                                                        {[TENANT_MAIN, TENANT_PREVIEW, TENANT_TARGET].includes(rollout.audienceKind) && <button type="button" className={buttonClass} onClick={() => void run(rollout.audienceKind === TENANT_TARGET ? "setTenantHold" : "setTrackHold", {
                                                            held: !rollout.held,
                                                            publicationId,
                                                            reason: rollout.held ? "Tenant cleared release hold" : "Tenant held release rollout",
                                                            ...(rollout.audienceKind === TENANT_TARGET ? {} : { trackId: rollout.trackId }),
                                                        })}>{rollout.held ? "Clear hold" : "Hold"}</button>}
                                                        {manual && (rollout.rolloutIds ?? []).map((rolloutId, index) => <button key={rolloutId} type="button" className={buttonClass} onClick={() => void run("advanceWave", { rolloutId })}>Advance lane {index + 1}</button>)}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!loading && visibleRollouts.length === 0 && (
                                    <tr><td colSpan="7" className="px-3 py-10 text-center text-[color:var(--muted)]">{showHistory ? "No rollout history in the loaded releases." : "No updates awaiting approval or in progress in the loaded releases. Existing general approvals are shown in Rollout history."}</td></tr>
                                )}
                            </tbody>
                        </table>
                        {loading && <div className="px-3 py-10 text-center text-[11px] text-[color:var(--muted)]">Loading rollout state...</div>}
                        {data.nextPageToken && (
                            <button
                                type="button"
                                onClick={() => void loadMore()}
                                disabled={loadingMore}
                                className="w-full px-3 py-2 text-center text-[9px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)] underline disabled:opacity-50"
                            >
                                {loadingMore ? "Loading" : `Load ${ARTIFACT_ROLLOUT_PAGE_SIZE} more`}
                            </button>
                        )}
                    </div>
                        </div>
                    )}

                    {tab === TAB_FLEET && (
                        <div className="grid gap-3">
                            <div className="grid gap-2 border border-[color:var(--line)] bg-[color:var(--panel)] p-3">
                                <div>
                                    <h2 className="text-[12px] font-medium text-[color:var(--ink)]">Your global wave schedule</h2>
                                    <p className="mt-1 max-w-[720px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                        How quickly a release spreads across your endpoints once it reaches you. Every
                                        component uses this one schedule, so saving it changes the pace for Agent,
                                        Content Updates and Managed Skills together. Each wave reaches a share of your
                                        endpoints and then waits before the next one opens; the wait is an observation
                                        window, not a health check. The last wave must reach 100%, and its wait is the
                                        final observation period before the release is complete. Saving affects
                                        releases published afterwards, not anything already moving.
                                    </p>
                                </div>
                                <label className="grid gap-1">
                                    <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Applies to</span>
                                    <select
                                        className={`${inputClass} max-w-[420px]`}
                                        aria-label="Schedule applies to"
                                        value={scheduleScope}
                                        onChange={(event) => changeScheduleScope(event.target.value)}
                                    >
                                        <option value="">Every component</option>
                                        {configurations.map((entry) => (
                                            <option key={entry.componentId} value={entry.componentId}>
                                                {componentLabel(entry.componentKey, entry.componentKind, entry.displayName)} only
                                            </option>
                                        ))}
                                    </select>
                                    {scheduleScope === CONTENT_UPDATES && <span className="text-[11px] text-[color:var(--muted)]">Applies to all {contentComponentIds.length} listed content modules. New versions keep this schedule; updates already moving keep their current schedule.</span>}
                                </label>
                                <div ref={editorRef} className="grid gap-1">
                                    <div className="grid grid-cols-[88px_1fr_28px] items-center gap-2 text-[9px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">
                                        <span>Reaches</span>
                                        <span>Then waits</span>
                                        <span />
                                    </div>
                                    {waveRows.map((row, index) => (
                                        <div key={index} className="grid grid-cols-[88px_1fr_28px] items-center gap-2">
                                            <div className="flex items-center gap-1">
                                                <input
                                                    className={`${inputClass} w-full`}
                                                    type="number"
                                                    min="1"
                                                    max="100"
                                                    value={row.percent}
                                                    aria-label={`Wave ${index + 1} share of endpoints`}
                                                    onChange={(event) => setRow(index, { percent: event.target.value })}
                                                />
                                                <span className="text-[11px] text-[color:var(--muted)]">%</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <input
                                                    className={`${inputClass} w-[80px]`}
                                                    type="number"
                                                    min="0"
                                                    value={row.amount}
                                                    aria-label={`Wave ${index + 1} wait`}
                                                    onChange={(event) => setRow(index, { amount: event.target.value })}
                                                />
                                                <select
                                                    className={inputClass}
                                                    value={row.unit}
                                                    aria-label={`Wave ${index + 1} wait unit`}
                                                    onChange={(event) => setRow(index, { unit: event.target.value })}
                                                >
                                                    {WAVE_UNITS.map((unit) => (
                                                        <option key={unit.label} value={unit.label}>{unit.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <button
                                                type="button"
                                                className={buttonClass}
                                                aria-label={`Remove wave ${index + 1}`}
                                                onClick={() => removeRow(index)}
                                            >
                                                -
                                            </button>
                                        </div>
                                    ))}
                                    <div className="mt-1 flex flex-wrap items-center gap-2">
                                        <button type="button" className={buttonClass} onClick={addRow}>Add wave</button>
                                        <button
                                            type="button"
                                            className={buttonClass}
                                            disabled={!parsedLadder}
                                            onClick={saveWaveLadder}
                                        >
                                            Save schedule
                                        </button>
                                        {/* Only in the global scope: scoped to one component, the
                                            way back is that component's own card. */}
                                        {!scheduleScope && followsGlobalSchedule.length > 0 && (
                                            <button
                                                type="button"
                                                className={buttonClass}
                                                onClick={clearGlobalSchedule}
                                            >
                                                Use Origin's defaults
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <p className="text-[11px] text-[color:var(--muted)]">
                                    {ladderHint}
                                </p>
                            </div>

                    {data.configurations.length > 0 && (
                        <div className="grid gap-2">
                            <div>
                                <h2 className="text-[12px] font-medium text-[color:var(--ink)]">Rollout schedule</h2>
                                <p className="mt-1 max-w-[720px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                    A release reaches the fleet in two stages. Origin widens it across tenants first,
                                    on its own schedule; once it reaches yours, the waves below widen it across your
                                    endpoints. Each endpoint's place in line is drawn from its ID and the release, so
                                    every release reshuffles the order and no endpoint is always first. The wait after a
                                    step is an observation window, not a health check: a stage advances when its time is
                                    up, whether or not endpoints have reported.
                                </p>
                                <p className="mt-1 max-w-[720px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                    The first stage is Origin's, because it moves a release across every tenant. Waves
                                    are yours: they move it across your endpoints only, and only once Origin has
                                    finished rolling the release out everywhere else, so slowing yours down holds
                                    nobody else up.
                                </p>
                            </div>
                            <div className="grid gap-3 lg:grid-cols-2">
                            {configurations.map((configuration) => (
                                <div key={configuration.componentId} className="border border-[color:var(--line)] bg-[color:var(--panel)] p-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <h2 className="text-[11px] font-medium text-[color:var(--ink)]" title={configuration.componentKey}>{componentLabel(configuration.componentKey, configuration.componentKind, configuration.displayName)}</h2>
                                        <span className="text-[9px] uppercase tracking-[0.1em] text-[color:var(--muted-soft)]">{configuration.environment}</span>
                                    </div>
                                    <div className="mt-2 grid gap-2 text-[10px] text-[color:var(--muted)]">
                                        <div className="grid gap-1">
                                            <span className="text-[color:var(--muted-soft)]">Waves, across your endpoints:</span>
                                            {configuration.mixedWaveSteps
                                                ? <p>Content updates currently use different schedules. Choose one shared schedule above.</p>
                                                : <StepList steps={configuration.waveSteps} unit="endpoints" />}
                                            {/* The editor below writes one schedule for every
                                                component, so this loads a starting point rather
                                                than editing this component alone. Without it a
                                                reader looking at the schedule they want to change
                                                had nothing to click, and when components disagree
                                                the editor starts empty with nothing to copy. */}
                                            <span className="text-[color:var(--muted-soft)]">
                                                {configuration.members && !configuration.waveScope
                                                    ? "Using different schedule settings"
                                                    : configuration.waveScope === "tenant_component"
                                                    ? "Using a schedule set for this component"
                                                    : configuration.waveScope === "tenant"
                                                      ? "Using your schedule"
                                                      : "Using Origin's default"}
                                            </span>
                                            {canControl && (configuration.waveScope === "tenant_component" || configuration.members?.some((entry) => entry.waveScope === "tenant_component")) && (
                                                <button
                                                    type="button"
                                                    className="justify-self-start text-[10px] text-[color:var(--muted)] underline"
                                                    onClick={() => void useGlobalSchedule(configuration.componentId)}
                                                >
                                                    Use the global schedule
                                                </button>
                                            )}
                                            {canControl && (configuration.waveSteps ?? []).length > 0 && (
                                                <button
                                                    type="button"
                                                    className="mt-1 justify-self-start text-[10px] text-[color:var(--muted)] underline"
                                                    onClick={() => editScheduleRows(configuration, false)}
                                                >
                                                    Edit this schedule
                                                </button>
                                            )}
                                            {canControl && (configuration.waveSteps ?? []).length > 0 && (
                                                <button
                                                    type="button"
                                                    className="justify-self-start text-[10px] text-[color:var(--muted)] underline"
                                                    onClick={() => editScheduleRows(configuration, true)}
                                                >
                                                    Give this component its own schedule
                                                </button>
                                            )}
                                        </div>
                                        {configuration.maximumAgeDays > 0 && (
                                            <div>
                                                <span className="text-[color:var(--muted-soft)]">Support window:</span> retired after {configuration.maximumAgeDays} days or {configuration.maximumNewerReleases} newer releases, whichever comes first
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            </div>
                        </div>
                    )}

                            <div className="grid gap-3 lg:grid-cols-2">
                                <div className="grid gap-2 border border-[color:var(--line)] bg-[color:var(--panel)] p-3">
                                    <div>
                                        <h2 className="text-[12px] font-medium text-[color:var(--ink)]">Endpoint override</h2>
                                        <p className="mt-1 text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                            Pin one endpoint to a track, or to a fixed place in the wave order. Without an
                                            override an endpoint's place is drawn from its ID and reshuffles every
                                            release; with one it is fixed, so a machine you watch closely can always go
                                            first and a fragile one can always go last.
                                        </p>
                                        <p className="mt-1 text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                            Wave number is a position in the schedule above, counting from 1, not a
                                            percentage: an endpoint set to 2 joins when the second wave opens. The
                                            position applies to every component, not one of them.
                                            {" "}If a component has fewer waves, the endpoint joins its final wave.
                                        </p>
                                    </div>
                                    <label className="grid gap-1">
                                        <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Search hostname</span>
                                        <input className={inputClass} placeholder="Search endpoints by hostname" value={endpointQuery} onChange={(event) => {
                                            setEndpointQuery(event.target.value);
                                            setEndpointId("");
                                            setEndpointEntryManual(false);
                                        }} />
                                    </label>
                                    <label className="grid gap-1">
                                        <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Endpoint</span>
                                        {/* A select rather than a datalist: browsers disagree about
                                            whether a datalist shows an option's label at all, and
                                            Firefox shows only the value, so the hostname could go
                                            missing on the control whose whole point is showing it. */}
                                        <select
                                            className={inputClass}
                                            aria-busy={endpointLoading}
                                            disabled={endpointLoading}
                                            value={endpointPickerValue}
                                            onChange={(event) => {
                                                const next = event.target.value;
                                                setEndpointEntryManual(next === MANUAL_ENDPOINT_ENTRY);
                                                setEndpointId(next === MANUAL_ENDPOINT_ENTRY ? "" : next);
                                            }}
                                        >
                                            <option value="">{endpointLoading ? "Searching endpoints..." : "Select endpoint"}</option>
                                            {endpointOptions.items.map((endpoint) => (
                                                <option key={endpoint.id} value={endpoint.id}>
                                                    {endpoint.hostname ? `${endpoint.hostname} - ${endpoint.id}` : endpoint.id}
                                                </option>
                                            ))}
                                            <option value={MANUAL_ENDPOINT_ENTRY}>Enter an ID manually...</option>
                                        </select>
                                        {endpointEntryManual && (
                                            <input
                                                className={inputClass}
                                                value={endpointId}
                                                onChange={(event) => setEndpointId(event.target.value)}
                                                placeholder="Endpoint UUID"
                                            />
                                        )}
                                        {endpointError && <span role="alert" className="text-[11px] text-[color:var(--danger)]">{endpointError}</span>}
                                        {!endpointLoading && !endpointError && endpointQuery && endpointOptions.total === 0 && <span className="text-[11px] text-[color:var(--muted)]">No endpoints match this hostname.</span>}
                                        {!endpointLoading && endpointOptions.total > endpointOptions.items.length && (
                                            <span className="text-[9px] text-[color:var(--muted-soft)]">
                                                Showing the first {endpointOptions.items.length} of {endpointOptions.total}{" "}
                                                endpoints. Narrow the hostname search or enter an ID manually.
                                            </span>
                                        )}
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <label className="grid gap-1">
                                            <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Track</span>
                                            <select className={inputClass} value={trackId} onChange={(event) => setTrackId(event.target.value)}>
                                                <option value="">Select track</option>
                                                {data.tracks.map((track) => <option key={track.trackId} value={track.trackId}>{track.name}</option>)}
                                            </select>
                                        </label>
                                        <label className="grid gap-1">
                                            <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Wave number</span>
                                            <input className={inputClass} type="number" min="1" max={MAX_WAVE_POSITION} step="1" value={wavePosition} onChange={(event) => setWavePosition(event.target.value)} />
                                            {!wavePositionValid && (
                                                <span className="text-[9px] text-[color:var(--danger)]">
                                                    Enter a whole wave number from 1 to {MAX_WAVE_POSITION}.
                                                </span>
                                            )}
                                        </label>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button type="button" className={buttonClass} disabled={!endpointId || !trackId} onClick={() => void setEndpointTrack()}>Assign track</button>
                                        <button type="button" className={buttonClass} disabled={!endpointId || !wavePositionValid} onClick={() => void setEndpointWave()}>Assign wave</button>
                                    </div>
                                </div>

                                <div className="grid gap-2 border border-[color:var(--line)] bg-[color:var(--panel)] p-3">
                                    <div>
                                        <h2 className="text-[12px] font-medium text-[color:var(--ink)]">Directory group override</h2>
                                        <p className="mt-1 text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                            The same override for everyone in a directory group, so membership decides
                                            rollout order and you do not maintain a list of machines. Groups come from
                                            your synced directories, and a rule follows the group: endpoints joining or
                                            leaving pick it up on the next sync. As with an endpoint override, the
                                            track and wave apply to every component.
                                        </p>
                                    </div>
                                    {!(directoryUnavailable || integrations.length === 0) && (
                                        <div className="grid grid-cols-2 gap-2">
                                            <label className="grid gap-1">
                                                <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Track</span>
                                                <select className={inputClass} value={groupTrackId} aria-label="Group track" onChange={(event) => setGroupTrackId(event.target.value)}>
                                                    <option value="">Select track</option>
                                                    {data.tracks.map((track) => <option key={track.trackId} value={track.trackId}>{track.name}</option>)}
                                                </select>
                                            </label>
                                            <label className="grid gap-1">
                                                <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Wave number</span>
                                                <input className={inputClass} type="number" min="1" max={MAX_WAVE_POSITION} step="1" aria-label="Group wave number" value={groupWavePosition} onChange={(event) => setGroupWavePosition(event.target.value)} />
                                                {!groupWavePositionValid && (
                                                    <span className="text-[9px] text-[color:var(--danger)]">
                                                        Enter a whole wave number from 1 to {MAX_WAVE_POSITION}.
                                                    </span>
                                                )}
                                            </label>
                                        </div>
                                    )}
                                    {directoryUnavailable || integrations.length === 0 ? (
                                        <p className="text-[11px] text-[color:var(--muted)]">
                                            {directoryUnavailable
                                                ? "Directory integrations could not be loaded, so groups are unavailable here."
                                                : "No directory integration is connected, so there are no synced groups to target."}
                                        </p>
                                    ) : (
                                        <>
                                            <label className="grid gap-1">
                                                <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Directory</span>
                                                <select
                                                    className={inputClass}
                                                    value={directoryIntegrationId}
                                                    onChange={(event) => {
                                                        setDirectoryIntegrationId(event.target.value);
                                                        setDirectoryGroupId("");
                                                    }}
                                                >
                                                    <option value="">Select directory</option>
                                                    {integrations.map((integration) => (
                                                        <option key={integration.id} value={integration.id}>
                                                            {integration.displayName || integration.provider || integration.id}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="grid gap-1">
                                                <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Group</span>
                                                <select
                                                    className={inputClass}
                                                    value={directoryGroupId}
                                                    disabled={!directoryIntegrationId}
                                                    onChange={(event) => setDirectoryGroupId(event.target.value)}
                                                >
                                                    <option value="">
                                                        {directoryIntegrationId ? "Select group" : "Select a directory first"}
                                                    </option>
                                                    {groups.items.map((group) => (
                                                        <option key={group.sourceId} value={group.sourceId}>
                                                            {group.displayName || group.sourceId}
                                                        </option>
                                                    ))}
                                                </select>
                                                {groups.truncated && (
                                                    <span className="text-[9px] text-[color:var(--muted-soft)]">
                                                        Showing the first {DIRECTORY_GROUP_PAGE_SIZE} groups.
                                                    </span>
                                                )}
                                            </label>
                                        </>
                                    )}
                                    <div className="grid grid-cols-2 gap-2">
                                        <button type="button" className={buttonClass} disabled={!directoryIntegrationId || !directoryGroupId || !groupTrackId} onClick={() => void setDirectoryGroupTrack()}>Assign track</button>
                                        <button type="button" className={buttonClass} disabled={!directoryIntegrationId || !directoryGroupId || !groupWavePositionValid} onClick={() => void setDirectoryGroupWave()}>Assign wave</button>
                                    </div>
                                </div>
                            </div>

                    {(data.endpointRules.length > 0 || data.directoryGroupRules.length > 0) && (
                        <div className="grid gap-3 lg:grid-cols-2">
                            <div className="border border-[color:var(--line)] bg-[color:var(--panel)] p-3">
                                <h2 className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Endpoint rules</h2>
                                <div className="mt-2 grid gap-1 font-mono text-[10px] text-[color:var(--muted)]">
                                    {data.endpointRules.map((rule) => (
                                        <div key={rule.endpointId} className="flex flex-wrap items-center justify-between gap-3">
                                            <span className="break-all" title={protoBytesToUuid(rule.endpointId)}>{rule.hostname || protoBytesToUuid(rule.endpointId)}</span>
                                            <span>track {protoBytesToUuid(rule.trackId) || "default"}, wave {rule.wavePosition || "adaptive"}</span>
                                            {canControl && <button type="button" disabled={saving} className={buttonClass} aria-label={`Remove endpoint override for ${rule.hostname || protoBytesToUuid(rule.endpointId)}`} onClick={() => {
                                                if (window.confirm(`Remove the track and wave overrides for ${rule.hostname || protoBytesToUuid(rule.endpointId)}? Applicable group rules and default rollout placement will take over.`)) void run("clearEndpointOverrides", { endpointId: rule.endpointId });
                                            }}>Remove override</button>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="border border-[color:var(--line)] bg-[color:var(--panel)] p-3">
                                <h2 className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Directory group rules</h2>
                                <div className="mt-2 grid gap-1 font-mono text-[10px] text-[color:var(--muted)]">
                                    {data.directoryGroupRules.map((rule) => (
                                        <div key={`${rule.directoryIntegrationId}:${rule.directoryGroupId}`} className="flex flex-wrap items-center justify-between gap-3">
                                            <span className="break-all">{directoryGroupLabel(rule)}</span>
                                            <span>track {protoBytesToUuid(rule.trackId) || "default"}, wave {rule.wavePosition || "adaptive"}</span>
                                            {canControl && <button type="button" disabled={saving} className={buttonClass} aria-label={`Remove directory override for ${directoryGroupLabel(rule)}`} onClick={() => {
                                                if (window.confirm(`Remove the track and wave overrides for ${directoryGroupLabel(rule)}? Other applicable rules and default rollout placement will take over.`)) void run("clearDirectoryGroupOverrides", { directoryGroupId: rule.directoryGroupId, directoryIntegrationId: rule.directoryIntegrationId });
                                            }}>Remove override</button>}
                                        </div>
                                    ))}
                                </div>
                                {data.assignmentRulesTruncated && <p className="mt-2 text-[9px] text-[color:var(--danger)]">Only the first 500 endpoint and group rules are shown.</p>}
                            </div>
                        </div>
                    )}
                        </div>
                    )}

                    <ConfirmDialog
                        open={pendingConfirm != null}
                        analyticsId={DIALOG.ROLLOUT_WAVE_CONFIRM}
                        eyebrow="Wave schedule"
                        title={pendingConfirm?.kind === "clear"
                            ? "Go back to Origin's default schedules?"
                            : "Put every component on this schedule?"}
                        body={pendingConfirm ? (
                            <div className="grid gap-[10px]">
                                <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                                    {pendingConfirm.kind === "clear"
                                        ? `${plural(pendingConfirm.moved.length, "component")} currently on your schedule ${pendingConfirm.moved.length === 1 ? "goes" : "go"} back to the default Origin ships for it, which differs by component:`
                                        : `${plural(pendingConfirm.moved.length, "component")} on a different schedule today ${pendingConfirm.moved.length === 1 ? "moves" : "move"} to this one:`}
                                </p>
                                <ul className="m-0 grid list-none gap-[2px] p-0 text-[12px] text-[color:var(--ink)]">
                                    {pendingConfirm.moved.map((name) => <li key={name}>{name}</li>)}
                                </ul>
                                <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                                    {pendingConfirm.kind === "clear"
                                        ? "A component you gave its own schedule keeps it. Releases already moving keep the schedule they started on."
                                        : "Releases already moving keep the schedule they started on."}
                                </p>
                            </div>
                        ) : null}
                        confirmLabel={pendingConfirm?.kind === "clear" ? "Use Origin's defaults" : "Save schedule"}
                        destructive={pendingConfirm?.kind === "clear"}
                        onConfirm={confirmPendingAction}
                        onCancel={() => setPendingConfirm(null)}
                    />

                    {tab === TAB_COMPONENTS && canControl && (
                        <div className="grid gap-3">
                            <div className="grid gap-2 border border-[color:var(--line)] bg-[color:var(--panel)] p-3">
                                <div>
                                    <h2 className="text-[12px] font-medium text-[color:var(--ink)]">Component</h2>
                                    <p className="mt-1 max-w-[720px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                        Pick the component the two sections below apply to. Your wave order and
                                        overrides are not affected by this choice.
                                    </p>
                                </div>
                                <select
                                    className={`${inputClass} max-w-[420px]`}
                                    aria-label="Component"
                                    value={componentId}
                                    onChange={(event) => setComponentId(event.target.value)}
                                >
                                    <option value="">Select component</option>
                                    {components.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                                </select>
                            </div>

                            <div className="grid gap-2 border border-[color:var(--line)] bg-[color:var(--panel)] p-3">
                                <div>
                                    <h2 className="text-[12px] font-medium text-[color:var(--ink)]">Release policy</h2>
                                    <p className="mt-1 max-w-[720px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                        What happens when Origin finishes rolling a release out to the fleet. On automatic,
                                        it starts moving through your endpoints on its own. On manual, it waits here until
                                        you start it. Major releases are asked separately because they are the ones most
                                        worth holding.
                                    </p>
                                </div>
                                <div className="grid gap-2 sm:grid-cols-2 lg:max-w-[560px]">
                                    <label className="grid gap-1">
                                        <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Minor releases</span>
                                        <select className={inputClass} value={minorPolicy} onChange={(event) => setMinorPolicy(event.target.value)}>
                                            {!minorPolicy && <option value="">Mixed settings - choose a shared policy</option>}
                                            <option value={POLICY_AUTOMATIC}>Start automatically</option>
                                            <option value={POLICY_MANUAL}>Wait for me</option>
                                        </select>
                                    </label>
                                    <label className="grid gap-1">
                                        <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">Major releases</span>
                                        <select className={inputClass} value={majorPolicy} onChange={(event) => setMajorPolicy(event.target.value)}>
                                            {!majorPolicy && <option value="">Mixed settings - choose a shared policy</option>}
                                            <option value={POLICY_AUTOMATIC}>Start automatically</option>
                                            <option value={POLICY_MANUAL}>Wait for me</option>
                                        </select>
                                    </label>
                                </div>
                                <div>
                                    <button type="button" className={buttonClass} disabled={!componentId || !majorPolicy || !minorPolicy} onClick={() => void setPolicy()}>Save policy</button>
                                </div>
                                {componentId === CONTENT_UPDATES && <p className="text-[11px] text-[color:var(--muted)]">Applies to all {contentComponentIds.length} listed content modules. New versions keep these settings.</p>}
                            </div>

                            <div className="grid gap-2 border border-[color:var(--line)] bg-[color:var(--panel)] p-3">
                                <div>
                                    <h2 className="text-[12px] font-medium text-[color:var(--ink)]">Tracks</h2>
                                    <p className="mt-1 max-w-[720px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                        Your endpoints sit on one of two tracks.
                                    </p>
                                    <dl className="mt-2 grid max-w-[720px] gap-1.5 text-[11px] leading-[1.45]">
                                        <div>
                                            <dt className="inline font-medium text-[color:var(--ink)]">Main.</dt>
                                            <dd className="inline text-[color:var(--muted)]">
                                                {" "}Where every endpoint is unless you move it. Releases arrive here under
                                                the policy above, then spread across these endpoints wave by wave.
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="inline font-medium text-[color:var(--ink)]">Preview.</dt>
                                            <dd className="inline text-[color:var(--muted)]">
                                                {" "}An opt-in group that takes a release ahead of main, so you can check it
                                                against your own software first. Everyone on it gets the release at once,
                                                with no waves. It is early relative to your other endpoints, not to Origin:
                                                a release only reaches preview after Origin has finished rolling it out
                                                across the fleet.
                                            </dd>
                                        </div>
                                    </dl>
                                    <p className="mt-2 max-w-[720px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                        Preview has to be turned on per component before endpoints on it receive that
                                        component early. Move endpoints onto it with an override below.
                                    </p>
                                </div>
                                {previewTrack ? (
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button type="button" className={buttonClass} disabled={!componentId} onClick={togglePreview}>
                                            {previewEnabled ? "Disable preview" : "Enable preview"}
                                        </button>
                                        <span className="text-[11px] text-[color:var(--muted)]">
                                            {!componentId
                                                ? "Select a component to see whether preview is on."
                                                : previewMixed
                                                    ? "On for some content updates. Enable preview to apply it to all."
                                                    : previewEnabled
                                                    ? "On for this component."
                                                    : "Off for this component."}
                                        </span>
                                    </div>
                                ) : (
                                    <p className="text-[11px] text-[color:var(--muted)]">
                                        This tenant has no preview track.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </fieldset>
            </div>
        </>
    );
}
